import { inject, Injectable } from '@angular/core';
import { CredentialsCurrentUser, CredentialsService } from '@app/core';
import {
    Client,
    ClientOption,
    ClientSetting,
    UsersBrands
} from '@app/core/services/api/API.service';
import { BehaviorSubject, forkJoin, from, Observable, map, of } from 'rxjs';
import { ClientsService } from '../unauth';
import {
    ClientOptionService,
    ClientSettingService,
    UsersBrandsService
} from '@app/core/api/services';
import { sortJSON } from '../../util.service';
import { tap } from 'rxjs/operators';

export interface UserClientContext {
    client: Client;
    clientOption: ClientOption;
    userBrands: UsersBrands[];
    clientSetting: ClientSetting | null;
}

@Injectable({
    providedIn: 'root'
})
/**
 * Service for get client and clientOptions in user session
 */
export class UserClientOptionService {
    private _clientsService = inject(ClientsService);
    private _clientOptionService = inject(ClientOptionService);
    private _clientSettingService = inject(ClientSettingService);
    private _usersBrandsService = inject(UsersBrandsService);
    private _credentialsService = inject(CredentialsService);

    private _client: BehaviorSubject<Client | null> = new BehaviorSubject(null);
    private _clientOption: BehaviorSubject<ClientOption | null> = new BehaviorSubject(
        null
    );
    private _clientSetting: BehaviorSubject<ClientSetting | null> = new BehaviorSubject(
        null
    );
    private _userBrands: BehaviorSubject<
        UsersBrands[] | null
    > = new BehaviorSubject(null);

    private _loadedClientScope: string | null = null;
    private _clientSettingPreloaded = false;
    private _loadedBrandsClientId: string | null = null;

    constructor() {}

    get client$(): Observable<Client> {
        return this._client.asObservable();
    }

    get clientOption$(): Observable<ClientOption> {
        return this._clientOption.asObservable();
    }

    get clientSetting$(): Observable<ClientSetting | null> {
        return this._clientSetting.asObservable();
    }

    get userBrands$(): Observable<UsersBrands[]> {
        return this._userBrands.asObservable();
    }

    getClientSnapshot(): Client | null {
        return this._client.getValue();
    }

    getClientOptionSnapshot(): ClientOption | null {
        return this._clientOption.getValue();
    }

    getClientSettingSnapshot(): ClientSetting | null {
        return this._clientSetting.getValue();
    }

    getUserBrandsSnapshot(): UsersBrands[] {
        return this._userBrands.getValue() ?? [];
    }

    /** Cliente, opciones y ClientSetting ya precargados para el clientId de sesión. */
    isClientContextPreloaded(clientId?: string | null): boolean {
        if (!clientId) {
            return false;
        }
        return (
            this._loadedClientScope === clientId &&
            this._clientSettingPreloaded &&
            !!this._client.getValue() &&
            !!this._clientOption.getValue()
        );
    }

    getClientAndClientOptions(): Observable<UserClientContext> {
        const credentialsCurrentUser = this._credentialsService
            .credentialsCurrentUser;
        if (!credentialsCurrentUser?.clientId) {
            return of({
                client: null,
                clientOption: null,
                userBrands: null,
                clientSetting: null
            });
        }

        const scopeKey = credentialsCurrentUser.clientId;
        const cachedClient = this._client.getValue();
        const cachedOption = this._clientOption.getValue();

        if (this.isClientContextPreloaded(scopeKey)) {
            return of({
                client: cachedClient,
                clientOption: cachedOption,
                userBrands: this.getUserBrandsSnapshot(),
                clientSetting: this.getClientSettingSnapshot()
            });
        }

        return forkJoin([
            this._clientsService.findClientById(scopeKey),
            this._clientOptionService.getClientOption(scopeKey),
            from(this._clientSettingService.getClientSetting(scopeKey))
        ]).pipe(
            map(
                ([client, clientOption, clientSetting]: [
                    Client,
                    ClientOption,
                    ClientSetting
                ]) => {
                    this._loadedClientScope = scopeKey;
                    this._clientSettingPreloaded = true;
                    this._client.next(client);
                    this._clientOption.next(clientOption);
                    this._clientSetting.next(clientSetting ?? null);
                    return {
                        client,
                        clientOption,
                        userBrands: this.getUserBrandsSnapshot(),
                        clientSetting: clientSetting ?? null
                    };
                }
            )
        );
    }

    /**
     * Marcas por cliente; solo necesario en shopping cart / admin, no en catálogo.
     */
    loadUserBrands(): Observable<UsersBrands[]> {
        const credentialsCurrentUser = this._credentialsService
            .credentialsCurrentUser;
        if (!credentialsCurrentUser?.clientId) {
            return of([]);
        }

        const clientId = credentialsCurrentUser.clientId;
        const cached = this._userBrands.getValue();
        if (
            this._loadedBrandsClientId === clientId &&
            cached &&
            cached.length > 0
        ) {
            return of(cached);
        }

        return from(
            this._usersBrandsService.getUsersBrandByClientId(clientId)
        ).pipe(
            tap((userBrands: UsersBrands[]) => {
                this._loadedBrandsClientId = clientId;
                this._userBrands.next(
                    sortJSON(userBrands ?? [], 'name', 'asc')
                );
            })
        );
    }

    clear(): void {
        this._loadedClientScope = null;
        this._clientSettingPreloaded = false;
        this._loadedBrandsClientId = null;
        this._client.next(null);
        this._clientOption.next(null);
        this._clientSetting.next(null);
        this._userBrands.next(null);
    }
}
